import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { TSlotCard } from '@lootboy/lib/slices/cardCollections/hooks/useSlotCards';

import ButtonClean from '~/components/ButtonClean';
import Image from '~/components/Image';
import { ESnackbarVariant } from '~/components/molecules/Snackbar';
import SnackbarMarkdown from '~/components/molecules/Snackbar/SnackbarMarkdown';
import { useSnackbar } from '~/components/organisms/SnackbarContext';

import { ReactComponent as CheckIcon } from '~/assets/images/icons/i-check-circle.svg';
import { ReactComponent as ArrowIcon } from '~/assets/images/iconsLibrary/arrow-up.svg';

import cardStyles from '../CardTile.module.scss';
import styles from './SlotCardDialogContent.module.scss';

interface ISlotCardDialogContentProps {
  onCardClick: (cardId: string) => void;
  cards: Array<TSlotCard>;
  selectedCard: { _id: string; image: string } | undefined;
}

enum ECardSortingMethod {
  newest = 'newest',
  rating = 'rating',
}

enum ECardSortingDirection {
  ascending = 'ascending',
  descending = 'descending',
}

const sortByDate = (a: TSlotCard, b: TSlotCard) => b._id.localeCompare(a._id);

const sortByRating = (a: TSlotCard, b: TSlotCard) => b.value - a.value;

const SlotCardDialogContent: React.FC<ISlotCardDialogContentProps> = ({
  onCardClick,
  cards,
  selectedCard,
}) => {
  const { pushSnackbar, dismissSnackbar } = useSnackbar();
  const [clickedCard, setClickedCard] = useState<string | null>(
    selectedCard?._id ?? null,
  );
  const [highlightedCard, setHighlightedCard] = useState<string | null>(null);
  const [sortingMethod, setSortingMethod] = useState(ECardSortingMethod.newest);
  const [sortingDirection, setSortingDirection] = useState(
    ECardSortingDirection.descending,
  );

  const handleAvailableCards = (cardId: string) => {
    setClickedCard(cardId === selectedCard?._id ? null : cardId);
    onCardClick(cardId);
  };

  const sortedCards = useMemo(() => {
    const sorted = [...cards].sort(
      sortingMethod === ECardSortingMethod.newest ? sortByDate : sortByRating,
    );
    return sortingDirection === ECardSortingDirection.ascending
      ? sorted
      : sorted.reverse();
  }, [cards, sortingDirection, sortingMethod]);

  const handleUnavailablePlacedCardClick = (
    card: TSlotCard & { placedInCollection: string; isAvailable: false },
  ) => {
    if (highlightedCard === card._id) {
      dismissSnackbar();
      setHighlightedCard(null);
      return;
    }

    dismissSnackbar().then(() => {
      pushSnackbar({
        onClose: () => setHighlightedCard(null),
        variant: ESnackbarVariant.WARNING,
        children: (
          <SnackbarMarkdown>
            {I18n.t('collections_sb_card_already_placed', {
              collectionId: card.placedInCollection,
            })}
          </SnackbarMarkdown>
        ),
      });
    });
    setHighlightedCard(card._id);
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.header}>
        {I18n.t('collections_bs_selector_headline')}
      </h3>
      <div className={styles.sorting}>
        <div className={styles.sortingMethods}>
          <ButtonClean
            onClick={() => {
              setSortingMethod(ECardSortingMethod.newest);
            }}
            className={classNames(styles.sortingMethodsButton, {
              [styles.active]: sortingMethod === ECardSortingMethod.newest,
            })}
          >
            {I18n.t('common_sorting_newest')}
          </ButtonClean>
          <ButtonClean
            onClick={() => {
              setSortingMethod(ECardSortingMethod.rating);
            }}
            className={classNames(styles.sortingMethodsButton, {
              [styles.active]: sortingMethod === ECardSortingMethod.rating,
            })}
          >
            {I18n.t('collections_rating')}
          </ButtonClean>
        </div>
        <ButtonClean
          aria-label={I18n.t('common_sorting')}
          className={classNames(
            styles.sortDirectionButton,
            styles[sortingDirection],
          )}
          onClick={() => {
            setSortingDirection((prevDir) =>
              prevDir === ECardSortingDirection.descending
                ? ECardSortingDirection.ascending
                : ECardSortingDirection.descending,
            );
          }}
        >
          <ArrowIcon />
        </ButtonClean>
      </div>
      <div className={styles.scrollWrapper}>
        <div className={styles.grid}>
          {/* Selected card has to be rendered first no matter the selected sorting */}
          {selectedCard !== undefined && (
            <ButtonClean
              className={classNames(cardStyles.card, styles.card, {
                [cardStyles.selected]: clickedCard === selectedCard._id,
              })}
              onClick={() => handleAvailableCards(selectedCard._id)}
            >
              <Image
                alt="Currently selected LootCard graphic"
                path={selectedCard.image}
                className={styles.cardGraphic}
              />
              <CheckIcon className={styles.checkIcon} />
            </ButtonClean>
          )}
          {sortedCards.map((card) => (
            <ButtonClean
              className={classNames(cardStyles.card, styles.card, {
                // adds border around the card and checkmark
                [cardStyles.selected]:
                  clickedCard === card._id || highlightedCard === card._id,
                // adds yellow background colors border yellow
                [cardStyles.placed]: highlightedCard === card._id,
                [cardStyles.disabled]: !card.isAvailable,
              })}
              key={card._id}
              disabled={!card.isAvailable && !card.placedInCollection}
              onClick={() => {
                if (!card.isAvailable && card.placedInCollection) {
                  handleUnavailablePlacedCardClick(card);
                } else {
                  handleAvailableCards(card._id);
                }
              }}
            >
              <Image
                alt="LootCard graphic"
                path={card.image}
                className={styles.cardGraphic}
              />
              <CheckIcon className={styles.checkIcon} />
            </ButtonClean>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SlotCardDialogContent;
